import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EditorialPage, handwritten } from "../components/editorial";
import { captureConversion } from "../lib/analytics";
import {
  ChoiceInput,
  FEATURE_OPTIONS,
  Honeypot,
  MultiChoiceInput,
  ScaleInput,
  TextInput,
  segmentToEnum,
  submitSurvey,
  usageAmountToEnum,
} from "../components/survey/SurveyInputs";

// /survey/full — LL-0184 beta feedback form, LONG variant (~25 questions).
// Unlisted, same as /survey. Universal (not per-group) — Group B (live UAT)
// still stays verbal per LL-0156.
//
// World-class survey design choices made here:
// - Sectioned into themed steps with a progress indicator, rather than one
//   endless scroll. 25 questions in one scroll is a wall; chunking by theme
//   (first impressions -> onboarding -> usage -> trust -> discovery -> wrap-up
//   -> optional contact) keeps each screen digestible and lets people feel
//   progress instead of dread.
// - EVERY question is optional. No validation blocks Next/Send. A persistent
//   "submit now" escape hatch is available on every step for anyone who wants
//   out before the end — nothing here should feel like homework.
// - Deliberate variety of question types (open text, labelled 1-5 scales,
//   single-choice, multi-choice) so answering doesn't feel repetitive, and so
//   different kinds of signal get captured the way they're best captured
//   (a scale for magnitude, multi-choice for "which of these", open text for
//   anything that needs a tester's own words).
// - Only 3 questions are tagged to the cross-group core metrics
//   (ease/satisfaction/referral) that also exist on the short form, so this
//   and /survey stay comparable on those even though everything else differs.
// - Contact info (name, phone or email) is its own final, clearly-optional
//   step — framed as "only if you want us to follow up", never implied as
//   required to submit feedback.

type ScoreField =
  "ease_score" | "satisfaction_score" | "referral_score" | "return_score";

type Question =
  | { id: string; kind: "text"; prompt: string }
  | {
      id: string;
      kind: "scale";
      prompt: string;
      lowLabel: string;
      highLabel: string;
      score?: ScoreField;
      followUp?: { id: string; prompt: string };
      // Only the true 0-10 NPS question (NPS1) overrides these — everything
      // else stays the standard labelled 1-5.
      min?: number;
      max?: number;
    }
  | { id: string; kind: "choice"; prompt: string; options: string[] }
  | {
      id: string;
      kind: "multichoice";
      prompt: string;
      options: string[];
      // Only shown once the respondent has reported more than a single open
      // (see USE1 in "About you") — a one-time opener doesn't have a real
      // basis to say which feature felt most valuable yet.
      minUsage?: "handful";
    };

type Section = { title: string; questions: Question[] };

const SECTIONS: Section[] = [
  {
    title: "About you",
    questions: [
      {
        id: "SEG1",
        kind: "choice",
        prompt: "Which of these is closest to you right now?",
        options: [
          "New to London",
          "Have a friend group but want more to do",
          "Struggle to make plans happen",
          "Just curious",
        ],
      },
      {
        id: "USE1",
        kind: "choice",
        prompt:
          "Since you got the app, how many times would you say you've opened it?",
        options: ["Just the once", "A handful of times", "Regularly"],
      },
    ],
  },
  {
    title: "First impressions",
    questions: [
      {
        id: "F1",
        kind: "text",
        prompt:
          "In one sentence: what did you think LetsLoop was for, before you opened it?",
      },
      {
        id: "F2",
        kind: "choice",
        prompt: "Where did you first hear about LetsLoop?",
        options: [
          "A friend",
          "Instagram",
          "TikTok",
          "A group chat",
          "Saw an ad or poster",
          "Somewhere else",
        ],
      },
      {
        id: "F3",
        kind: "text",
        prompt: "What were you most excited to try, if anything?",
      },
      {
        id: "F4",
        kind: "text",
        prompt: "What almost stopped you from trying it, if anything?",
      },
    ],
  },
  {
    title: "Getting started",
    questions: [
      {
        id: "F5",
        kind: "scale",
        prompt: "How easy was signing up?",
        lowLabel: "Really hard",
        highLabel: "Effortless",
      },
      {
        id: "F6",
        kind: "choice",
        prompt: "Did you understand what to do first, without needing help?",
        options: ["Yes", "Kind of", "No"],
      },
      {
        id: "F7",
        kind: "text",
        prompt: "What's the first thing you tried to do in the app?",
      },
      {
        id: "F8",
        kind: "choice",
        prompt: "Did it work the way you expected?",
        options: ["Yes", "Kind of", "No"],
      },
      {
        id: "F9",
        kind: "text",
        prompt: "What confused you, if anything, in your first few minutes?",
      },
      {
        id: "F9b",
        kind: "scale",
        prompt: "How confusing was getting started, overall?",
        lowLabel: "Not at all",
        highLabel: "Very confusing",
      },
    ],
  },
  {
    title: "Using it",
    questions: [
      {
        id: "F10",
        kind: "multichoice",
        prompt: "What did you actually end up using the app for?",
        options: FEATURE_OPTIONS,
      },
      {
        id: "FEAT1",
        kind: "multichoice",
        prompt: "Which one thing felt most worth having?",
        options: FEATURE_OPTIONS,
        minUsage: "handful",
      },
      {
        id: "FEAT2",
        kind: "multichoice",
        prompt:
          "Which of these, if any, did you find confusing or hard to use?",
        options: [...FEATURE_OPTIONS, "None of these"],
      },
      {
        id: "BUG1",
        kind: "choice",
        prompt:
          "Did you run into any bugs, crashes, freezes, or things that just didn't work?",
        options: [
          "No, nothing like that",
          "Yes, something didn't work right",
          "Not sure / can't remember",
        ],
      },
      {
        id: "BUG1b",
        kind: "text",
        prompt:
          "(Optional) If yes: what happened, and what were you doing at the time?",
      },
      {
        id: "F11",
        kind: "choice",
        prompt: "How often do you think you'd realistically open this app?",
        options: [
          "Daily",
          "A few times a week",
          "Weekly",
          "Occasionally",
          "Probably not much",
        ],
      },
      {
        id: "F12",
        kind: "scale",
        prompt: "How easy was it to do the main thing you wanted to do?",
        lowLabel: "Really hard",
        highLabel: "Effortless",
        score: "ease_score",
      },
      {
        id: "F13",
        kind: "scale",
        prompt: "Overall, how would you rate the experience?",
        lowLabel: "Poor",
        highLabel: "Excellent",
        score: "satisfaction_score",
      },
      {
        id: "F14",
        kind: "text",
        prompt: "What's the one feature you'd add or fix first?",
      },
      {
        id: "F15",
        kind: "text",
        prompt: "Anything that felt unnecessary, or like clutter?",
      },
    ],
  },
  {
    title: "Trust & comfort",
    questions: [
      {
        id: "F16",
        kind: "scale",
        prompt:
          "How comfortable did you feel about safety and trust, using the app with people you don't know?",
        lowLabel: "Not at all comfortable",
        highLabel: "Very comfortable",
      },
      {
        id: "F17",
        kind: "choice",
        prompt:
          "Would you feel comfortable meeting someone through LetsLoop in person?",
        options: ["Yes", "Not sure", "No"],
      },
      {
        id: "F18",
        kind: "text",
        prompt:
          "Was there anything that felt risky, sketchy, or off about how the app handles your info or other people's?",
      },
      {
        id: "F19",
        kind: "text",
        prompt: "What would make you trust the app more?",
      },
    ],
  },
  {
    title: "Discovery & spontaneity",
    questions: [
      {
        id: "F20",
        kind: "text",
        prompt:
          "Think of the last time you wanted to do something social or spontaneous, what stopped you or made it hard?",
      },
      {
        id: "F21",
        kind: "choice",
        prompt: "Does LetsLoop make that easier, harder, or about the same?",
        options: [
          "Much easier",
          "A bit easier",
          "About the same",
          "Harder",
          "Not sure yet",
        ],
      },
      {
        id: "F22",
        kind: "text",
        prompt: "What would make you open this again tomorrow?",
      },
    ],
  },
  {
    title: "Last few",
    questions: [
      {
        id: "PMF1",
        kind: "choice",
        prompt: "How would you feel if you could no longer use LetsLoop?",
        options: [
          "Very disappointed",
          "Somewhat disappointed",
          "Not disappointed",
          "N/A, I don't really use it",
        ],
      },
      {
        id: "NOTF1",
        kind: "choice",
        prompt:
          "Be honest: is this actually useful for you, or were you just being nice trying it?",
        options: ["Yes, it's useful", "Trying to be nice", "Not sure yet"],
      },
      {
        id: "PAY1",
        kind: "choice",
        prompt:
          "If LetsLoop had a paid tier with extra features, would you consider it?",
        options: ["Yes", "Maybe", "No"],
      },
      {
        id: "F23",
        kind: "scale",
        prompt: "How likely are you to tell a friend about LetsLoop?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "referral_score",
      },
      {
        id: "NPS1",
        kind: "scale",
        prompt:
          "On a scale of 0-10, how likely are you to recommend LetsLoop to a friend or colleague?",
        lowLabel: "Not at all likely",
        highLabel: "Extremely likely",
        min: 0,
        max: 10,
      },
      {
        id: "RET1",
        kind: "scale",
        prompt: "How likely are you to open it again this week?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "return_score",
        followUp: { id: "RET1b", prompt: "(Optional) Why or why not?" },
      },
      {
        id: "AFT1",
        kind: "text",
        prompt:
          "Now that you've used it, how would you describe LetsLoop to a friend in one sentence?",
      },
      {
        id: "GUT1",
        kind: "text",
        prompt: "One word for how using LetsLoop made you feel?",
      },
      {
        id: "F25",
        kind: "text",
        prompt: "Anything else you want to tell us, good or bad?",
      },
    ],
  },
];

const TOTAL_STEPS = SECTIONS.length + 1; // + the contact step

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

const SurveyFullPage = () => {
  const [searchParams] = useSearchParams();
  const group = (searchParams.get("g") || "").trim().toUpperCase();
  const urlName = (searchParams.get("n") || "").trim();
  // Stable tester identifier, independent of contactName below (which the
  // tester can freely edit on the contact step) — see LL-0184 NORTH-STAR.md.
  const slug = (searchParams.get("s") || "").trim();

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [multiAnswers, setMultiAnswers] = useState<Record<string, string[]>>(
    {},
  );
  const [scores, setScores] = useState<Record<ScoreField, number | null>>({
    ease_score: null,
    satisfaction_score: null,
    referral_score: null,
    return_score: null,
  });
  const [contactName, setContactName] = useState(urlName);
  const [contactReach, setContactReach] = useState("");
  const [honeypot, setHoneypot] = useState("");
  // Guards against a real mobile-browser bug (Josh, 2026-07-06): tapping
  // "Next" on the last question step re-renders that exact same button slot
  // into the "Send it" (type=submit) button, and on a real phone the
  // in-flight touch/click from the Next tap can land on the new button
  // before the finger's even lifted, auto-submitting the form the instant
  // the last step appears — without the user ever tapping Send. A synthetic
  // Playwright click doesn't reproduce this (it dispatches directly to a
  // resolved element, bypassing the real touch pipeline), so this wasn't
  // caught by the earlier click-through verification. Fix has two parts:
  // distinct `key`s on the Next/Send-it buttons below (forces React to
  // unmount+remount instead of mutating the same DOM node's type attribute
  // in place), plus this short "not yet interactive" window as a second
  // layer of defence.
  const [submitArmed, setSubmitArmed] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );

  useEffect(() => {
    document.title = "LetsLoop feedback";
  }, []);

  useEffect(() => {
    setSubmitArmed(false);
    const t = setTimeout(() => setSubmitArmed(true), 400);
    return () => clearTimeout(t);
  }, [step]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (state === "loading") return;
    setState("loading");
    try {
      const combinedAnswers: Record<string, string> = { ...answers };
      for (const [id, vals] of Object.entries(multiAnswers) as [
        string,
        string[],
      ][]) {
        if (vals.length > 0) combinedAnswers[id] = vals.join(", ");
      }
      const reach = contactReach.trim();
      const ok = await submitSurvey({
        group: group || "FULL",
        formVariant: "full",
        testerName: contactName || urlName,
        testerSlug: slug,
        contactEmail: reach && EMAIL_RE.test(reach) ? reach : undefined,
        contactPhone: reach && !EMAIL_RE.test(reach) ? reach : undefined,
        easeScore: scores.ease_score,
        satisfactionScore: scores.satisfaction_score,
        referralScore: scores.referral_score,
        returnScore: scores.return_score,
        usageAmount: usageAmountToEnum(answers.USE1),
        segment: segmentToEnum(answers.SEG1),
        answers: combinedAnswers,
        honeypot,
      });
      if (!ok) throw new Error("submit failed");
      setState("done");
      captureConversion("beta_survey_submit", {
        group: group || "FULL",
        form_variant: "full",
      });
    } catch {
      setState("error");
    }
  };

  const shortLinkParams = new URLSearchParams();
  shortLinkParams.set("g", group || "A");
  if (urlName) shortLinkParams.set("n", urlName);
  if (slug) shortLinkParams.set("s", slug);
  const shortLink = `/beta-review?${shortLinkParams.toString()}`;

  const renderQuestion = (q: Question, i: number) => (
    <div key={q.id} className="mb-7">
      <label className="block text-ink font-semibold mb-2.5 leading-snug">
        {i + 1}. {q.prompt}
      </label>
      {q.kind === "text" && (
        <TextInput
          value={answers[q.id] || ""}
          onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
        />
      )}
      {q.kind === "choice" && (
        <ChoiceInput
          options={q.options}
          value={answers[q.id]}
          onChange={(opt) => setAnswers((a) => ({ ...a, [q.id]: opt }))}
        />
      )}
      {q.kind === "multichoice" && (
        <MultiChoiceInput
          options={q.options}
          values={multiAnswers[q.id] || []}
          onChange={(next) => setMultiAnswers((a) => ({ ...a, [q.id]: next }))}
        />
      )}
      {q.kind === "scale" && (
        <>
          <ScaleInput
            value={
              q.score
                ? scores[q.score]
                : answers[q.id] !== undefined && answers[q.id] !== ""
                  ? Number(answers[q.id])
                  : null
            }
            onChange={(n) => {
              setAnswers((a) => ({ ...a, [q.id]: String(n) }));
              if (q.score) setScores((s) => ({ ...s, [q.score!]: n }));
            }}
            lowLabel={q.lowLabel}
            highLabel={q.highLabel}
            min={q.min}
            max={q.max}
          />
          {q.followUp && (
            <TextInput
              value={answers[q.followUp.id] || ""}
              onChange={(v) =>
                setAnswers((a) => ({ ...a, [q.followUp!.id]: v }))
              }
              rows={1}
              placeholder={q.followUp.prompt}
            />
          )}
        </>
      )}
    </div>
  );

  // "Just the once" respondents skip questions that need real usage to
  // answer honestly (see FEAT1's minUsage tag) — everything else is shown to
  // everyone, since most questions here are still answerable meaningfully
  // even from a single open (per world-class-survey-review-2026-07-05.md).
  const usageAnswer = answers.USE1;
  const visibleQuestions = (qs: Question[]) =>
    qs.filter(
      (q) =>
        !("minUsage" in q && q.minUsage) || usageAnswer !== "Just the once",
    );

  return (
    <EditorialPage>
      <div className="max-w-xl mx-auto px-6 md:px-0 pt-12 pb-24">
        <div className="mb-8">
          <Link to="/" aria-label="LetsLoop home">
            <img
              src="/letsloop-logo.png"
              alt="LetsLoop"
              className="h-7 w-auto"
            />
          </Link>
        </div>

        {state === "done" ? (
          <div className="border-2 border-ink bg-paper-tan p-6 shadow-[6px_6px_0_0_rgba(5,5,5,1)]">
            <p className="font-serif text-2xl text-ink">Thank you.</p>
            <p className="text-gray-700 mt-1">
              Really appreciate you taking the time. It goes straight to the
              founders.
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <Honeypot value={honeypot} setValue={setHoneypot} />

            <div className="flex items-center justify-between mb-2">
              <p
                className="text-2xl md:text-3xl text-salmon -rotate-2 origin-left"
                style={handwritten}
              >
                hey{urlName ? ` ${urlName}` : ""}
              </p>
              <span className="text-xs text-gray-500">
                Step {step + 1} of {TOTAL_STEPS}
              </span>
            </div>
            <div className="h-1.5 bg-paper-tan border border-ink mb-6">
              <div
                className="h-full bg-salmon transition-all"
                style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
              />
            </div>

            {step === 0 && (
              <p className="text-sm text-gray-600 mb-8">
                About 5 minutes, nothing here is required, answer whatever you
                fancy and skip the rest. In a rush?{" "}
                <Link to={shortLink} className="underline hover:text-salmon">
                  Take the 2-minute version instead
                </Link>
                .
              </p>
            )}

            {step < SECTIONS.length ? (
              <>
                <h1 className="font-serif text-ink tracking-tight leading-[1.05] text-2xl md:text-3xl mb-6">
                  {SECTIONS[step].title}
                </h1>
                {visibleQuestions(SECTIONS[step].questions).map((q, idx) => {
                  const priorCount = SECTIONS.slice(0, step).reduce(
                    (n, s) => n + visibleQuestions(s.questions).length,
                    0,
                  );
                  return renderQuestion(q, priorCount + idx);
                })}
              </>
            ) : (
              <>
                <h1 className="font-serif text-ink tracking-tight leading-[1.05] text-2xl md:text-3xl mb-2">
                  One more thing (optional)
                </h1>
                <p className="text-sm text-gray-600 mb-6">
                  Only fill this in if you're happy for us to follow up with you
                  directly. Completely optional either way, thank you.
                </p>
                <div className="mb-5">
                  <label className="block text-ink font-semibold mb-2.5">
                    Your name
                  </label>
                  <TextInput
                    value={contactName}
                    onChange={setContactName}
                    rows={1}
                  />
                </div>
                <div className="mb-2">
                  <label className="block text-ink font-semibold mb-2.5">
                    Best way to reach you, phone or email
                  </label>
                  <TextInput
                    value={contactReach}
                    onChange={setContactReach}
                    rows={1}
                  />
                </div>
              </>
            )}

            {/* "Submit now, skip the rest" moved onto its own centered line,
                clearly separated from Back/Next — found live (Adrien,
                2026-07-06) that sitting it directly between the two primary
                buttons made it too easy to mis-tap on a phone, silently
                submitting early and skipping the remaining questions AND the
                contact step. Same action, safer placement. */}
            <div className="text-center mt-8 mb-3">
              <button
                type="button"
                onClick={() => submit()}
                disabled={state === "loading"}
                className="text-sm text-gray-500 underline hover:text-salmon"
              >
                Submit now, skip the rest
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="h-11 px-5 border-2 border-ink font-medium disabled:opacity-30"
              >
                Back
              </button>
              {step < TOTAL_STEPS - 1 ? (
                <button
                  key="step-next"
                  type="button"
                  onClick={() =>
                    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))
                  }
                  className="h-11 px-5 border-2 border-ink bg-ink text-white font-semibold"
                >
                  Next
                </button>
              ) : (
                <button
                  key="step-submit"
                  type="submit"
                  disabled={state === "loading" || !submitArmed}
                  className="h-11 px-5 border-2 border-ink bg-ink text-white font-semibold shadow-[4px_4px_0_0_rgba(255,160,122,1)] disabled:opacity-40 disabled:shadow-none"
                >
                  {state === "loading" ? "Sending…" : "Send it"}
                </button>
              )}
            </div>
            {state === "error" && (
              <p className="text-sm text-red-600 mt-3">
                Something went wrong, mind trying again in a sec?
              </p>
            )}
          </form>
        )}
      </div>
    </EditorialPage>
  );
};

export default SurveyFullPage;
